import { getSSRTranslation } from '@/i18n.ts';
import {
  EnhancedLightboxItem,
  FeedQueryResult,
  adjustFotoscapesLink,
} from '../../net/softbox-types.ts';
import { CarouselCardProps } from './CarouselCard/CarouselCard';
import { getCurrencySymbol } from '@utils/currency.ts';
import { appendQuerystringParams } from '@moment/layouts/MainFeed/utils.ts';
import { AnalyticsService } from '@moment/analytics/AnalyticsService.ts'; // Extend LightboxItem to include product-specific properties

// Extend LightboxItem to include product-specific properties
interface ProductItem extends EnhancedLightboxItem {
  product?: {
    cta: any;
    brand: string;
    price: number;
    currency: string;
  };
}

export class CarouselItemFactory {
  private i18n: any;
  private feedData: FeedQueryResult;
  private searchParams: URLSearchParams;
  private partner: string;
  private analytics: AnalyticsService;

  constructor(
    feedData: FeedQueryResult,
    i18n: any,
    partner: string,
    searchParms: URLSearchParams,
    analytics: AnalyticsService
  ) {
    this.feedData = feedData;
    this.i18n = i18n;
    this.searchParams = searchParms;
    this.partner = partner;
    this.analytics = analytics;
  }

  private handleClick = (item: EnhancedLightboxItem) => {
    if (item.link) {
      const link = adjustFotoscapesLink(
        ['article', 'standard', 'product'].includes(item.lbtype)
          ? appendQuerystringParams(
              item.linkToContent(this.partner),
              this.searchParams
            )
          : item.linkToContent(this.partner)
      );

      this.analytics.emitLinkClickedEvent({
        partnerId: this.partner || '',
        originUrl: window.location.href,
        destinationUrl: link,
        searchParams: this.searchParams,
      });

      window.open(link, '_blank');
    }
  };

  /**
   * Gets the primary interest name for an item
   */
  private getPrimaryInterestName(item: EnhancedLightboxItem): string {
    if (!item.interests || item.interests.length === 0) {
      return '';
    }

    const primaryInterestUid = item.interests[0];
    const interest = this.feedData.interests?.interests?.find(
      (i) => {
        return i.uid === primaryInterestUid;
      }
    );

    if (!interest) {
      return '';
    }

    return getSSRTranslation(interest.name, this.i18n);
  }

  /**
   * Creates a product carousel item from a LightboxItem
   */
  private createProductItem(item: ProductItem): CarouselCardProps | null {
    const title = getSSRTranslation(item.title, this.i18n) || '';
    const imageSrc = item.images?.[0]?.link || '';
    const imageAlt = title;

    // Extract product-specific data
    const price = item.product?.price?.toString() || '';
    const currencyCode = item.product?.currency || 'USD';
    const localizedCurrency = getCurrencySymbol(currencyCode);

    // Only return if we have required data
    if (!title || !imageSrc) {
      return null;
    }

    return {
      type: 'product' as const,
      imageSrc,
      imageAlt,
      title,
      price,
      currency: localizedCurrency,
      contentId: item.uid,
      contentOwner: item.owner || '',
      primaryInterest: this.getPrimaryInterestName(item),
      originalItem: item,
      onClick: () => {
        this.handleClick(item);
      },
    };
  }

  /**
   * Creates an article carousel item from a LightboxItem
   */
  private createArticleItem(
    item: EnhancedLightboxItem
  ): CarouselCardProps | null {
    const title = getSSRTranslation(item.title, this.i18n) || '';
    const imageSrc = item.images?.[0]?.link || '';
    const imageAlt = title;
    const publisherName = item.owner || '';
    const publisherLogo = item.brandLogo || '';
    const darkPublisherLogo = item.brandLogoDark || '';

    // Only return if we have required data
    if (!title || !imageSrc) {
      return null;
    }

    return {
      type: 'article' as const,
      imageSrc,
      imageAlt,
      title,
      publisherName,
      publisherLogo,
      darkPublisherLogo,
      contentId: item.uid,
      contentOwner: item.owner || '',
      primaryInterest: this.getPrimaryInterestName(item),
      originalItem: item,
      onClick: () => {
        this.handleClick(item);
      },
    };
  }

  /**
   * Creates product carousel items from feed data
   */
  public createProductItems(): CarouselCardProps[] {
    if (!this.feedData?.news?.items) {
      return [];
    }

    return this.feedData.news.items
      .filter((item) => {
        return item.lbtype === 'product';
      })
      .map((item) => {
        return this.createProductItem(
          new EnhancedLightboxItem(item) as ProductItem
        );
      })
      .filter((item): item is CarouselCardProps => {
        return item !== null;
      });
  }

  /**
   * Creates article carousel items from feed data
   */
  public createArticleItems(): CarouselCardProps[] {
    if (!this.feedData?.news?.items) {
      return [];
    }

    return this.feedData.news.items
      .map((item) => {
        return this.createArticleItem(new EnhancedLightboxItem(item));
      })
      .filter((item): item is CarouselCardProps => {
        return item !== null;
      });
  }

  /**
   * Creates all carousel items (both products and articles) if needed in the future
   */
  public createAllItems(): CarouselCardProps[] {
    if (!this.feedData?.news?.items) {
      return [];
    }

    return this.feedData.news.items
      .map((item) => {
        if (item.lbtype === 'product') {
          return this.createProductItem(
            new EnhancedLightboxItem(item) as ProductItem
          );
        } else {
          return this.createArticleItem(new EnhancedLightboxItem(item));
        }
      })
      .filter((item): item is CarouselCardProps => {
        return item !== null;
      });
  }
}
